import { brandAsset } from '@/lib/brand';

/** One "how to get started" line: the action, and what it means. */
export interface AnnouncementStep {
  title: string;
  detail?: string;
}

/** One announcement for the What's new card. */
export interface Announcement {
  /** Stable key: dismissing hides this id for good, a new id shows again. */
  id: string;
  title: string;
  body: string;
  /** The short muted loop the card plays. */
  videoUrl: string;
  /** The full-size cut opened from the card; the loop when absent. */
  fullVideoUrl?: string;
  posterUrl?: string;
  /** Numbered "how to get started" lines, shown beside the full video. */
  steps?: AnnouncementStep[];
  cta?: { label: string; href: string };
}

/** The announcements on the What's new card, in order. */
export const RELEASES: Announcement[] = [
  {
    id: 'clu-launch-v1',
    title: 'Meet Clu, your GTM agent',
    body: 'Describe the job in plain English. Clu builds the agent, finds the people, and reports back in Slack.',
    videoUrl: '/whats-new/clu-v1-card.mp4',
    fullVideoUrl: '/whats-new/clu-v1.mp4',
    posterUrl: brandAsset('/whats-new/clu-v1-poster.jpg'),
    steps: [
      {
        title: 'Describe the job',
        detail:
          '"Every week, find 500 people who match my ICP and email them."',
      },
      {
        title: 'Review the agent',
        detail: 'Clu proposes the steps. Change anything before it runs.',
      },
      {
        title: 'Approve and go',
        detail: 'It runs on your schedule and reports back in Slack.',
      },
    ],
    cta: { label: 'Try Clu', href: '/clu' },
  },
  {
    id: 'sequences-launch-v1',
    title: 'Email, LinkedIn and calls in one sequence',
    body: 'Ask Clu for the sequence. Every email is written for one person, sent from your inbox, and a reply stops it.',
    videoUrl: '/whats-new/sequences-v1-card.mp4',
    fullVideoUrl: '/whats-new/sequences-v1.mp4',
    posterUrl: brandAsset('/whats-new/sequences-v1-poster.jpg'),
    steps: [
      {
        title: 'Start from a list',
        detail: 'Pick a lead list, or let Clu find the people first.',
      },
      {
        title: 'Ask for the sequence',
        detail: 'Email, LinkedIn and calls, written for each person.',
      },
      {
        title: 'Confirm and send',
        detail: 'Goes out from your inbox. A reply stops it.',
      },
    ],
    cta: { label: 'Open Sequences', href: '/sequences' },
  },
];

// v2: the first version marked ids when the card opened, so people who only
// looked at it never closed it. Starting a new list shows it to everyone once
// more, until they close it. Bump this to do that again.
const DISMISSED_KEY_PREFIX = 'cl_whats_new_dismissed_v2_';

// The card opens by itself once per announcement: a reload or the next
// sign-in leaves it shut, and the box still says it is unread.
const AUTO_OPENED_KEY_PREFIX = 'cl_whats_new_auto_opened_v1_';

function readIds(prefix: string, userId: string | null): string[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(`${prefix}${userId}`);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids)
      ? ids.filter((id): id is string => typeof id === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeIds(prefix: string, userId: string, ids: string[]): void {
  try {
    const stored = readIds(prefix, userId);
    const added = ids.filter((id) => !stored.includes(id));
    if (!added.length) return;
    localStorage.setItem(
      `${prefix}${userId}`,
      JSON.stringify([...stored, ...added])
    );
  } catch {
    // Storage blocked: the card comes back next visit, nothing breaks.
  }
}

/** Announcement ids this user closed, on this browser. */
export const readDismissed = (userId: string | null) =>
  readIds(DISMISSED_KEY_PREFIX, userId);

export const writeDismissed = (userId: string, ids: string[]) =>
  writeIds(DISMISSED_KEY_PREFIX, userId, ids);

/** Announcement ids the card already opened for by itself, on this browser. */
export const readAutoOpened = (userId: string | null) =>
  readIds(AUTO_OPENED_KEY_PREFIX, userId);

export const writeAutoOpened = (userId: string, ids: string[]) =>
  writeIds(AUTO_OPENED_KEY_PREFIX, userId, ids);
